import {
    BarChartOutlined,
    CalendarOutlined,
    BankOutlined,
    FileTextOutlined,
    SearchOutlined,
    DownloadOutlined,
    PrinterOutlined,
    EyeOutlined,
    PieChartOutlined,
    TableOutlined,
    ReloadOutlined,
    FileExcelOutlined,
    FilePdfOutlined
} from '@ant-design/icons';
import {
    ConfigProvider,
    Select,
    Skeleton,
    Tabs,
    Card,
    Table,
    Space,
    Button,
    Row,
    Col,
    Statistic,
    Tag,
    Tooltip,
    Empty,
    Divider,
    Progress,
    Modal,
    message,
    Dropdown
} from 'antd';
import frFR from "antd/locale/fr_FR";
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import StatistiquesSAPStyle from './StatistiquesSAPStyle';
import {
    statistiquesGlobalesColumns,
    detailSinistresColumns,
    resumeParBeneficiaireColumns
} from './StatistiquesSAPData';
import { DetailSinistreModal } from './DetailSinistreModal';
import { RepartitionChart } from './RepartitionChart';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import axiosInstance from '../../../../variables/api/axiosInstance';
import SideBar from '../../../../components/SideBar';
import NavBar from '../../../../components/NavBar';
import Footer from '../../../../components/Footer';

import { exportSAPToExcel, exportSAPToPDF, printSAPReport } from './StatistiquesSAPExport';

const StatistiquesSAP = () => {
    // États pour les filtres
    const [exercices, setExercices] = useState([]);
    const [selectedExercice, setSelectedExercice] = useState(null);
    const [categories, setCategories] = useState([]);
    const [selectedCategorie, setSelectedCategorie] = useState(null);
    const [compagnies, setCompagnies] = useState([]);
    const [selectedCompagnie, setSelectedCompagnie] = useState(null);

    // États pour les données
    const [loading, setLoading] = useState(false);
    const [statistiquesGlobales, setStatistiquesGlobales] = useState([]);
    const [detailSinistres, setDetailSinistres] = useState([]);
    const [resumeParBeneficiaire, setResumeParBeneficiaire] = useState([]);

    // États pour les modals
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedSinistre, setSelectedSinistre] = useState(null);

    // Onglet actif
    const [activeTab, setActiveTab] = useState("1");

    // Chargement initial
    useEffect(() => {
        Promise.all([
            axiosInstance.get('/api/stats/exercices'),
            axiosInstance.get('/api/categorie'),
            axiosInstance.get('/api/organisme/assurance')
        ])
            .then(([exercicesRes, catRes, compRes]) => {
                console.log('exercicesRes.data', exercicesRes.data);
                setExercices(exercicesRes.data);

                // Traitement des catégories
                const excludedCodes = ["A", "B", "T"];
                const filtered = catRes.data.filter(
                    (c) => !c.codeCategorie.startsWith("V") && !excludedCodes.includes(c.codeCategorie)
                );
                const hasTPVCategories = catRes.data.some((c) => excludedCodes.includes(c.codeCategorie));
                if (hasTPVCategories) {
                    filtered.push({ codeCategorie: "TPV", nom: "TPV", synthetic: true });
                }
                setCategories(filtered);

                setCompagnies(compRes.data);

                // Sélectionner l'exercice le plus récent
                if (exercicesRes.data.length > 0) {
                    setSelectedExercice(exercicesRes.data[0].exercice);
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des données");
            });
    }, []);

    // Charger les statistiques quand les filtres changent
    useEffect(() => {
        if (selectedExercice) {
            chargerStatistiques();
        }
    }, [selectedExercice, selectedCategorie, selectedCompagnie]);

    const chargerStatistiques = async () => {
        setLoading(true);
        try {
            const params = {
                exercice: selectedExercice,
                categorieCode: selectedCategorie || undefined,
                compagnieCode: selectedCompagnie || undefined
            };

            const [statsRes, detailRes, resumeRes] = await Promise.all([
                axiosInstance.get('/api/stats', { params }),
                axiosInstance.get('/api/stats/detail', { params }),
                axiosInstance.get('/api/stats/par-beneficiaire', {
                    params: {
                        exercice: selectedExercice,
                        categorieCode: selectedCategorie
                    }
                })
            ]);
            console.log('statsRes.data', statsRes.data);
            setStatistiquesGlobales(statsRes.data);
            console.log('detailRes.data', detailRes.data);
            setDetailSinistres(detailRes.data);
            console.log('resumeRes.data', resumeRes.data);
            setResumeParBeneficiaire(resumeRes.data);
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des statistiques");
        } finally {
            setLoading(false);
        }
    };

    // Calculs des totaux
    const calculerTotaux = () => {
        const totaux = {
            nombreSinistres: 0,
            montantSinistres: 0,
            montantSap: 0,
            montantReglement: 0
        };

        statistiquesGlobales.forEach(stat => {
            totaux.nombreSinistres += stat.nombreSinistres || 0;
            totaux.montantSap += stat.montantTotalSap || 0;
            totaux.montantReglement += (stat.montantTotalEvaluation - stat.montantTotalSap) || 0;
        });

        return totaux;
    };

    const totaux = calculerTotaux();

    // Handlers pour l'export
    const handleExportExcel = () => {
        if (statistiquesGlobales.length === 0) {
            message.warning('Aucune donnée à exporter');
            return;
        }

        message.loading('Génération du fichier Excel...', 1);

        try {
            exportSAPToExcel(
                statistiquesGlobales,
                detailSinistres,
                resumeParBeneficiaire,
                compagniesBeneficiaires,
                {
                    exercice: selectedExercice,
                    categorie: selectedCategorie,
                    compagnie: selectedCompagnie
                }
            );
            message.success('Export Excel généré avec succès');
        } catch (error) {
            console.error('Erreur export Excel:', error);
            message.error('Erreur lors de la génération du fichier Excel');
        }
    };

    const handleExportPDF = () => {
        if (statistiquesGlobales.length === 0) {
            message.warning('Aucune donnée à exporter');
            return;
        }

        message.loading('Génération du fichier PDF...', 1);

        try {
            exportSAPToPDF(
                statistiquesGlobales,
                detailSinistres,
                resumeParBeneficiaire,
                compagniesBeneficiaires,
                {
                    exercice: selectedExercice,
                    categorie: selectedCategorie,
                    compagnie: selectedCompagnie
                },
                totaux
            );
            message.success('Export PDF généré avec succès');
        } catch (error) {
            console.error('Erreur export PDF:', error);
            message.error('Erreur lors de la génération du fichier PDF');
        }
    };

    const handlePrint = () => {
        if (statistiquesGlobales.length === 0) {
            message.warning('Aucune donnée à imprimer');
            return;
        }

        try {
            printSAPReport(
                statistiquesGlobales,
                detailSinistres,
                resumeParBeneficiaire,
                compagniesBeneficiaires,
                {
                    exercice: selectedExercice,
                    categorie: selectedCategorie,
                    compagnie: selectedCompagnie
                },
                totaux
            );
        } catch (error) {
            console.error('Erreur impression:', error);
            message.error('Erreur lors de la préparation de l\'impression');
        }
    };

    // Menu dropdown pour l'export
    const exportMenuItems = [
        {
            key: 'excel',
            label: 'Exporter en Excel',
            icon: <FileExcelOutlined style={{ color: '#52c41a' }} />,
            onClick: handleExportExcel
        },
        {
            key: 'pdf',
            label: 'Exporter en PDF',
            icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
            onClick: handleExportPDF
        },
        {
            type: 'divider'
        },
        {
            key: 'print',
            label: 'Imprimer',
            icon: <PrinterOutlined />,
            onClick: handlePrint
        }
    ];

    // Extraction des compagnies bénéficiaires
    const getCompagniesBeneficiaires = () => {
        const compagniesSet = new Map();

        statistiquesGlobales.forEach(stat => {
            stat.repartitionParBeneficiaire?.forEach(rep => {
                if (!compagniesSet.has(rep.beneficiaireCode)) {
                    compagniesSet.set(rep.beneficiaireCode, {
                        code: rep.beneficiaireCode,
                        nom: rep.beneficiaireNom,
                        taux: rep.tauxRepartition
                    });
                }
            });
        });

        return Array.from(compagniesSet.values()).sort((a, b) => a.code.localeCompare(b.code));
    };

    const compagniesBeneficiaires = getCompagniesBeneficiaires();

    // Handlers
    const handleVoirDetail = (record) => {
        console.log('recordSinistre', record);
        setSelectedSinistre(record);
        setDetailModalVisible(true);
    };

    const resetFilters = () => {
        setSelectedCategorie(null);
        setSelectedCompagnie(null);
    };

    // Fonction pour générer le summary du tableau Vue globale
    const generateStatistiquesSummary = (pageData, compagniesBeneficiaires) => {
        // Calculer les totaux
        let totalSinistres = 0;
        let totalSap = 0;

        // Totaux par bénéficiaire
        const totauxParBeneficiaire = {};
        compagniesBeneficiaires.forEach(comp => {
            totauxParBeneficiaire[comp.code] = 0;
        });

        pageData.forEach(record => {
            totalSinistres += record.nombreSinistres || 0;
            totalSap += record.montantTotalSap || 0;

            // Calculer les totaux par bénéficiaire
            record.repartitionParBeneficiaire?.forEach(rep => {
                if (totauxParBeneficiaire[rep.beneficiaireCode] !== undefined) {
                    totauxParBeneficiaire[rep.beneficiaireCode] += rep.montantSAP || 0;
                }
            });
        });

        // Nombre de colonnes de base (avant les bénéficiaires)
        const baseColCount = 5;

        return (
            <Table.Summary fixed>
                <Table.Summary.Row style={{ background: '#fafafa' }}>
                    <Table.Summary.Cell index={0} colSpan={3}>
                        <strong style={{ fontSize: 13 }}>
                            📊 TOTAL ({pageData.length} ligne(s))
                        </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="center">
                        <strong style={{ color: '#1890ff', fontSize: 14 }}>
                            {totalSinistres}
                        </strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                        <strong style={{ color: '#ff4d4f', fontSize: 14 }}>
                            {displayNumbers(totalSap)} F
                        </strong>
                    </Table.Summary.Cell>
                    {compagniesBeneficiaires.map((comp, idx) => (
                        <Table.Summary.Cell
                            key={`summary-${comp.code}`}
                            index={baseColCount + idx}
                            align="right"
                        >
                            <strong style={{ color: '#722ed1', fontSize: 13 }}>
                                {displayNumbers(totauxParBeneficiaire[comp.code] || 0)} F
                            </strong>
                        </Table.Summary.Cell>
                    ))}
                </Table.Summary.Row>

                {/* Ligne de vérification: Total SAP = Somme des répartitions */}
                <Table.Summary.Row style={{ background: '#f0f5ff' }}>
                    <Table.Summary.Cell index={0} colSpan={baseColCount}>
                        <span style={{ fontSize: 11, color: '#666' }}>
                            ✓ Vérification: Somme des répartitions
                        </span>
                    </Table.Summary.Cell>
                    {compagniesBeneficiaires.map((comp, idx) => {
                        const pourcentage = totalSap > 0
                            ? (totauxParBeneficiaire[comp.code] / totalSap * 100)
                            : 0;
                        return (
                            <Table.Summary.Cell
                                key={`verif-${comp.code}`}
                                index={baseColCount + idx}
                                align="right"
                            >
                                <span style={{ fontSize: 11, color: '#1890ff' }}>
                                    {pourcentage.toFixed(2)}%
                                </span>
                            </Table.Summary.Cell>
                        );
                    })}
                </Table.Summary.Row>
            </Table.Summary>
        );
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <StatistiquesSAPStyle />

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: '#f0f2f5' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* Header */}
                            <div className="sap-page-header">
                                <div className="header-content">
                                    <div>
                                        <h1>
                                            <BarChartOutlined /> Statistiques SAP / Compagnie
                                        </h1>
                                        <p>
                                            États statistiques des Sinistres À Payer par exercice, compagnie et catégorie
                                        </p>
                                    </div>
                                    <Space>
                                        <Dropdown
                                            menu={{ items: exportMenuItems }}
                                            placement="bottomRight"
                                        >
                                            <Button
                                                type="primary"
                                                icon={<DownloadOutlined />}
                                                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                                            >
                                                Exporter
                                            </Button>
                                        </Dropdown>
                                        <Button
                                            icon={<PrinterOutlined />}
                                            onClick={handlePrint}
                                        >
                                            Imprimer
                                        </Button>
                                    </Space>
                                </div>
                            </div>

                            {/* Filtres */}
                            <Card className="filters-card" style={{ marginBottom: 24 }}>
                                <Row gutter={[16, 16]} align="middle">
                                    <Col xs={24} sm={12} md={6}>
                                        <div className="filter-item">
                                            <label><CalendarOutlined /> Exercice</label>
                                            <Select
                                                placeholder="Sélectionner un exercice"
                                                style={{ width: '100%' }}
                                                value={selectedExercice}
                                                onChange={setSelectedExercice}
                                                options={exercices.map(ex => ({
                                                    value: ex.exercice,
                                                    label: (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <span>{ex.exercice}</span>
                                                            <Tag color="blue">{ex.nombreSinistres} sinistres</Tag>
                                                        </div>
                                                    )
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <div className="filter-item">
                                            <label><FileTextOutlined /> Catégorie</label>
                                            <Select
                                                placeholder="Toutes les catégories"
                                                style={{ width: '100%' }}
                                                value={selectedCategorie}
                                                onChange={setSelectedCategorie}
                                                allowClear
                                                options={categories.map(cat => ({
                                                    value: cat.codeCategorie,
                                                    label: cat.nom
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <div className="filter-item">
                                            <label><BankOutlined /> Compagnie cédante</label>
                                            <Select
                                                placeholder="Toutes les compagnies"
                                                style={{ width: '100%' }}
                                                value={selectedCompagnie}
                                                onChange={setSelectedCompagnie}
                                                allowClear
                                                options={compagnies.map(comp => ({
                                                    value: comp.code,
                                                    label: comp.raisonSociale
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Space style={{ marginTop: 22 }}>
                                            <Button
                                                type="primary"
                                                icon={<SearchOutlined />}
                                                onClick={chargerStatistiques}
                                                loading={loading}
                                            >
                                                Rechercher
                                            </Button>
                                            <Button icon={<ReloadOutlined />} onClick={resetFilters}>
                                                Réinitialiser
                                            </Button>
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>

                            {/* Cartes de résumé */}
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                <Col xs={24} sm={12} lg={8}>
                                    <Card className="stat-card">
                                        <Statistic
                                            title="Nombre de sinistres"
                                            value={totaux.nombreSinistres}
                                            prefix={<FileTextOutlined style={{ color: '#1890ff' }} />}
                                            valueStyle={{ color: '#1890ff' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} lg={8}>
                                    <Card className="stat-card">
                                        <Statistic
                                            title="Total sinistres payés"
                                            value={displayNumbers(totaux.montantReglement)}
                                            suffix="F"
                                            prefix={<BarChartOutlined style={{ color: '#ff4d4f' }} />}
                                            valueStyle={{ color: '#ff4d4f' }}
                                        />
                                    </Card>
                                </Col>
                                {/* <Col xs={24} sm={12} lg={6}>
                                    <Card className="stat-card">
                                        <Statistic
                                            title="Total recours"
                                            value={displayNumbers(totaux.montantRecours)}
                                            suffix="F"
                                            prefix={<BarChartOutlined style={{ color: '#52c41a' }} />}
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Card>
                                </Col> */}
                                <Col xs={24} sm={12} lg={8}>
                                    <Card className="stat-card stat-card-highlight">
                                        <Statistic
                                            title="Montant SAP"
                                            value={displayNumbers(totaux.montantSap)}
                                            suffix="F"
                                            prefix={<PieChartOutlined style={{ color: '#722ed1' }} />}
                                            valueStyle={{ color: '#722ed1', fontWeight: 700 }}
                                        />
                                    </Card>
                                </Col>
                            </Row>

                            {/* Répartition par compagnie bénéficiaire */}
                            {/* {compagniesBeneficiaires.length > 0 && (
                                <Card
                                    title={
                                        <Space>
                                            <PieChartOutlined />
                                            <span>Répartition par compagnie bénéficiaire</span>
                                        </Space>
                                    }
                                    style={{ marginBottom: 24 }}
                                >
                                    <Row gutter={[16, 16]}>
                                        {compagniesBeneficiaires.map(comp => {
                                            const totalBenef = resumeParBeneficiaire
                                                .filter(r => r.beneficiaireCode === comp.code)
                                                .reduce((sum, r) => sum + (r.montantNetRepartis || 0), 0);

                                            const pourcentage = totaux.montantNet > 0
                                                ? (totalBenef / totaux.montantNet) * 100
                                                : 0;

                                            return (
                                                <Col key={comp.code} xs={24} sm={12} md={8} lg={6}>
                                                    <div className="beneficiaire-card">
                                                        <div className="beneficiaire-header">
                                                            <BankOutlined />
                                                            <span>{comp.nom}</span>
                                                        </div>
                                                        <div className="beneficiaire-taux">
                                                            Taux: {(comp.taux || 0).toFixed(2)}%
                                                        </div>
                                                        <div className="beneficiaire-montant">
                                                            {displayNumbers(totalBenef)} F
                                                        </div>
                                                        <Progress
                                                            percent={pourcentage}
                                                            showInfo={false}
                                                            strokeColor="#722ed1"
                                                            size="small"
                                                        />
                                                        <div className="beneficiaire-percent">
                                                            {pourcentage.toFixed(1)}% du total
                                                        </div>
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </Card>
                            )} */}

                            {/* Onglets principaux */}
                            <Card>
                                <ConfigProvider locale={frFR}>
                                    <Skeleton loading={loading} active>
                                        <Tabs
                                            activeKey={activeTab}
                                            onChange={setActiveTab}
                                            items={[

                                                {
                                                    label: (
                                                        <span>
                                                            <FileTextOutlined /> Détail des sinistres
                                                        </span>
                                                    ),
                                                    key: "1",
                                                    children: (
                                                        <Table
                                                            columns={detailSinistresColumns(compagniesBeneficiaires, handleVoirDetail)}
                                                            dataSource={detailSinistres}
                                                            rowKey="sinistreCodeId"
                                                            pagination={{
                                                                pageSize: 20,
                                                                showSizeChanger: true,
                                                                showTotal: (total) => `${total} sinistre(s)`
                                                            }}
                                                            scroll={{ x: 1600 }}
                                                            bordered
                                                            size="small"
                                                        />
                                                    )
                                                },
                                                {
                                                    label: (
                                                        <span>
                                                            <BankOutlined /> Par compagnie
                                                        </span>
                                                    ),
                                                    key: "2",
                                                    children: (
                                                        <Table
                                                            columns={resumeParBeneficiaireColumns()}
                                                            dataSource={resumeParBeneficiaire}
                                                            rowKey={(record) => `${record.exercice}-${record.beneficiaireCode}-${record.categorieCode}`}
                                                            pagination={{
                                                                pageSize: 10,
                                                                showSizeChanger: true,
                                                                showTotal: (total) => `${total} ligne(s)`
                                                            }}
                                                            scroll={{ x: 1200 }}
                                                            bordered
                                                            size="middle"
                                                            expandable={{
                                                                expandedRowRender: (record) => (
                                                                    <div style={{ padding: 16, background: '#fafafa' }}>
                                                                        <h4>Détail par compagnie cédante:</h4>
                                                                        <Row gutter={[16, 16]}>
                                                                            {record.detailParCedante?.map(detail => (
                                                                                <Col key={detail.cedanteCode} span={8}>
                                                                                    <Card size="small">
                                                                                        <div><strong>{detail.cedanteNom}</strong></div>
                                                                                        <div>Sinistres: {detail.nombreSinistres}</div>
                                                                                        {/* <div>Taux: {displayNumbers(detail.tauxRepartition)} %</div> */}
                                                                                        <div>SAP: {displayNumbers(detail.montantSAP)} F</div>
                                                                                    </Card>
                                                                                </Col>
                                                                            ))}
                                                                        </Row>
                                                                    </div>
                                                                )
                                                            }}
                                                        />
                                                    )
                                                },
                                                {
                                                    label: (
                                                        <span>
                                                            <TableOutlined /> Vue globale
                                                        </span>
                                                    ),
                                                    key: "3",
                                                    children: (
                                                        <Table
                                                            columns={statistiquesGlobalesColumns(compagniesBeneficiaires)}
                                                            dataSource={statistiquesGlobales}
                                                            rowKey={(record) => `${record.exercice}-${record.compagnieCedanteCode}-${record.categorieCode}`}
                                                            pagination={{
                                                                pageSize: 10,
                                                                showSizeChanger: true,
                                                                showTotal: (total) => `${total} ligne(s)`
                                                            }}
                                                            scroll={{ x: 1400 }}
                                                            bordered
                                                            size="middle"
                                                            summary={(pageData) => generateStatistiquesSummary(pageData, compagniesBeneficiaires)}
                                                        />
                                                    )
                                                },
                                                {
                                                    label: (
                                                        <span>
                                                            <PieChartOutlined /> Graphiques
                                                        </span>
                                                    ),
                                                    key: "4",
                                                    children: (
                                                        <RepartitionChart
                                                            data={resumeParBeneficiaire}
                                                            compagniesBeneficiaires={compagniesBeneficiaires}
                                                        />
                                                    )
                                                }
                                            ]}
                                        />
                                    </Skeleton>
                                </ConfigProvider>
                            </Card>
                        </div>

                        <Footer />
                    </div>
                </div>
            </div>

            {/* Modal détail sinistre */}
            <DetailSinistreModal
                visible={detailModalVisible}
                onClose={() => setDetailModalVisible(false)}
                sinistre={selectedSinistre}
                compagniesBeneficiaires={compagniesBeneficiaires}
            />
        </div>
    );
};

export default StatistiquesSAP;